/**
 * @fileoverview ClubOS Scouting Pipeline page — Kanban-style transfer target tracker
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Mock data
import { scoutingTargets, financialSummary } from "data/mockData";

// India locale
import { formatCompact } from "utils/indiaLocale";

// TODO: Replace with API call when backend is ready
// import { scoutingService } from "api";

const STAGES = [
  { key: "Identified",  color: "#e3f2fd", border: "#90caf9", badge: "secondary" },
  { key: "Watched",     color: "#f3e5f5", border: "#ce93d8", badge: "info" },
  { key: "Scouted",     color: "#e8f5e9", border: "#a5d6a7", badge: "success" },
  { key: "Targeted",    color: "#fff3e0", border: "#ffcc80", badge: "warning" },
  { key: "Approached",  color: "#fce4ec", border: "#f48fb1", badge: "error" },
  { key: "Agreed",      color: "#e8f5e9", border: "#66bb6a", badge: "success" },
];

const PRIORITY_COLOR = { High: "error", Medium: "warning", Low: "success" };

function ScoutingPipeline() {
  const [targets, setTargets] = useState(scoutingTargets);

  // TODO: Uncomment when API is ready
  // useEffect(() => { scoutingService.getPipeline().then(setTargets).catch(console.error); }, []);

  const budget = financialSummary.transferBudgetRemaining;
  const totalFeeEstimate = targets
    .filter((t) => t.stage !== "Identified" && t.feeEstimate)
    .reduce((acc, t) => acc + t.feeEstimate, 0);
  const agreed = targets.filter((t) => t.stage === "Agreed").length;
  const highPriority = targets.filter((t) => t.priority === "High").length;

  const moveStage = (id, direction) => {
    setTargets((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const idx = STAGES.findIndex((s) => s.key === t.stage);
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= STAGES.length) return t;
        return { ...t, stage: STAGES[newIdx].key };
      })
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Scouting Pipeline
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Transfer target tracker · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Transfer Budget" }}
                count={formatCompact(budget)}
                percentage={{ color: "info", text: "remaining" }}
                icon={{ color: "info", component: "account_balance_wallet" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Total Targets" }}
                count={String(targets.length)}
                percentage={{ color: "dark", text: "in pipeline" }}
                icon={{ color: "dark", component: "visibility" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "High Priority" }}
                count={String(highPriority)}
                percentage={{ color: "error", text: "flagged" }}
                icon={{ color: "error", component: "flag" }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard
                title={{ text: "Agreed" }}
                count={String(agreed)}
                percentage={{ color: "success", text: "deals done" }}
                icon={{ color: "success", component: "handshake" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Budget affordability bar */}
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={2.5}>
              <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Committed Fee Estimates vs Remaining Budget
                </SoftTypography>
                <SoftTypography variant="caption" color={totalFeeEstimate > budget ? "error" : "success"}>
                  {formatCompact(totalFeeEstimate)} / {formatCompact(budget)}
                </SoftTypography>
              </SoftBox>
              <SoftBox
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e9ecef",
                  overflow: "hidden",
                }}
              >
                <SoftBox
                  sx={{
                    height: "100%",
                    width: `${Math.min((totalFeeEstimate / budget) * 100, 100)}%`,
                    backgroundColor: totalFeeEstimate > budget ? "#f44336" : "#17c1e8",
                    borderRadius: 4,
                    transition: "width 0.4s ease",
                  }}
                />
              </SoftBox>
            </SoftBox>
          </Card>
        </SoftBox>

        {/* Kanban board */}
        <SoftBox sx={{ overflowX: "auto" }}>
          <SoftBox display="flex" gap={2} sx={{ minWidth: STAGES.length * 220 + (STAGES.length - 1) * 16, pb: 2 }}>
            {STAGES.map((stage) => {
              const stageTargets = targets.filter((t) => t.stage === stage.key);
              return (
                <SoftBox
                  key={stage.key}
                  flex="0 0 220px"
                  minWidth={220}
                  sx={{
                    backgroundColor: stage.color,
                    border: `1px solid ${stage.border}`,
                    borderRadius: "12px",
                    p: 1.5,
                  }}
                >
                  {/* Column header */}
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      {stage.key.toUpperCase()}
                    </SoftTypography>
                    <SoftBox
                      sx={{
                        backgroundColor: stage.border,
                        borderRadius: "50%",
                        width: 22,
                        height: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <SoftTypography variant="caption" fontWeight="bold" color="white" sx={{ fontSize: "0.65rem" }}>
                        {stageTargets.length}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  {/* Cards */}
                  {stageTargets.length === 0 ? (
                    <SoftBox
                      sx={{
                        border: `1px dashed ${stage.border}`,
                        borderRadius: "8px",
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <SoftTypography variant="caption" color="text">
                        No targets
                      </SoftTypography>
                    </SoftBox>
                  ) : (
                    stageTargets.map((target) => {
                      const affordable = !target.feeEstimate || target.feeEstimate <= budget;
                      return (
                        <SoftBox
                          key={target.id}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: "10px",
                            p: 1.5,
                            mb: 1,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            border: `1px solid ${affordable ? "#e9ecef" : "#ffccbc"}`,
                          }}
                        >
                          {/* Name + priority */}
                          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ fontSize: "0.8rem", lineHeight: 1.3 }}>
                              {target.name}
                            </SoftTypography>
                            <SoftBadge
                              badgeContent={target.priority}
                              color={PRIORITY_COLOR[target.priority]}
                              size="xs"
                              container
                            />
                          </SoftBox>

                          {/* Club + nationality */}
                          <SoftTypography variant="caption" color="text" display="block" mb={0.5}>
                            {target.flag} {target.club}
                          </SoftTypography>

                          {/* Position + age */}
                          <SoftBox display="flex" gap={0.5} mb={1} flexWrap="wrap">
                            <SoftBadge badgeContent={target.position} color="info" size="xs" container />
                            <SoftTypography variant="caption" color="text" alignSelf="center">
                              Age {target.age}
                            </SoftTypography>
                          </SoftBox>

                          {/* Fee estimate */}
                          {target.feeEstimate && (
                            <SoftBox
                              sx={{
                                backgroundColor: affordable ? "#f1f8e9" : "#fff3e0",
                                borderRadius: "6px",
                                px: 1,
                                py: 0.5,
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Icon sx={{ fontSize: "0.75rem", color: affordable ? "#388e3c" : "#e65100" }}>
                                {affordable ? "check_circle" : "warning"}
                              </Icon>
                              <SoftTypography variant="caption" fontWeight="bold" color={affordable ? "success" : "warning"}>
                                {formatCompact(target.feeEstimate)} est.
                              </SoftTypography>
                            </SoftBox>
                          )}

                          {/* Move buttons */}
                          <SoftBox display="flex" justifyContent="space-between" gap={0.5}>
                            <SoftButton
                              variant="outlined"
                              color="secondary"
                              size="small"
                              sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: "0.65rem" }}
                              onClick={() => moveStage(target.id, -1)}
                              disabled={stage.key === "Identified"}
                            >
                              ← Back
                            </SoftButton>
                            <SoftButton
                              variant="gradient"
                              color="info"
                              size="small"
                              sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: "0.65rem" }}
                              onClick={() => moveStage(target.id, 1)}
                              disabled={stage.key === "Agreed"}
                            >
                              Next →
                            </SoftButton>
                          </SoftBox>
                        </SoftBox>
                      );
                    })
                  )}
                </SoftBox>
              );
            })}
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default ScoutingPipeline;
